/* eslint-disable no-unused-vars */
import { Lunar } from '../../services/lunar.js';

// =========================================================================
// 1. 常量定义 (CONSTANTS)
// =========================================================================

const GAN = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
const ZHI = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];

/* BD-d单柱判断；BD-s 双柱判断；BN属相年命判断；BT 类型判断 */
const RULES_BAD = [
  
  { code: "BD1", category: "奇门", name: "阴差阳错", desc: "丙开头到癸日，诸事不宜", yiji: "百事不宜" },
  { code: "BS1", category: "五行", name: "日干入时墓", desc: "天干入墓于地支", yiji: "昏迷，迷糊" },
  { code: "BS2", category: "奇门", name: "五不遇时", desc: "时干克日干，且阳克阳阴克阴（七杀）", yiji: "百事不宜" },
  { code: "BS3", category: "五行", name: "天克地冲", desc: "天干和地支互为冲克", yiji: "百事不宜，除非离散" },
  { code: "BN1", category: "属相", name: "日冲生肖", desc: "地支冲克用户属相", yiji: "诸事不宜" }
];
/* GQ-奇门规则 */
const RULES_GOOD = [
  { code: "GQ1", category: "奇门", name: "天显时格", desc: "奇门六甲旬首，大吉", yiji: "天时、地利、人和" },
  { code: "GQ2", category: "奇门", name: "玉女守门", desc: "丁为玉女，守门大吉", yiji: "吉利" }
];

// 阴差阳错规则表
const YIN_CHA_YANG_CUO = [
  "丙子", "丁丑", "戊寅", "辛卯", "壬辰", "癸巳",
  "丙午", "丁未", "戊申", "辛酉", "壬戌", "癸亥"
];

// 天干入墓 (天干 -> 墓地支)
// 木(甲乙)->未, 火(丙丁)土(戊己)->戌, 金(庚辛)->丑, 水(壬癸)->辰
const GAN_MU = {
  "甲": "未", "乙": "未",
  "丙": "戌", "丁": "戌",
  "戊": "戌", "己": "戌",
  "庚": "丑", "辛": "丑",
  "壬": "辰", "癸": "辰"
};

const ZODIAC_MAP = {
  "鼠": "子", "牛": "丑", "虎": "寅", "兔": "卯",
  "龙": "辰", "蛇": "巳", "马": "午", "羊": "未",
  "猴": "申", "鸡": "酉", "狗": "戌", "猪": "亥"
};

const ZHI_CHONG_MAP = {
  "子": "午", "丑": "未", "寅": "申", "卯": "酉", "辰": "戌", "巳": "亥",
  "午": "子", "未": "丑", "申": "寅", "酉": "卯", "戌": "辰", "亥": "巳"
};

// =========================================================================
// 2. 内部工具方法 (INTERNAL UTILS)
// =========================================================================

const _getGanIndex = (gan) => GAN.indexOf(gan);
const _getZhiIndex = (zhi) => ZHI.indexOf(zhi);



// =========================================================================
// 3. 规则校验器 (RULE CHECKERS)
// =========================================================================
// BD1: 阴差阳错
const _check_BD1_YinChaYangCuo = (hourGanZhi) => {
  return YIN_CHA_YANG_CUO.includes(hourGanZhi) ? RULES_BAD.find(r => r.code === "BD1") : null;
};
// BS1: 日干入时墓
const _check_BS1_RiGanRuShiMu = (dayGan, hourZhi) => {
  return (GAN_MU[dayGan] === hourZhi) ? RULES_BAD.find(r => r.code === "BS1") : null;
};
// BS2: 五不遇时
const _check_BS2_WuBuYuShi = (dayGan, hourGan) => {
  const dayIdx = _getGanIndex(dayGan);
  const hourIdx = _getGanIndex(hourGan);
  // 七杀且同性: (日干 + 6) % 10 == 时干
  return ((dayIdx + 6) % 10 === hourIdx) ? RULES_BAD.find(r => r.code === "BS2") : null;
};
// BS3: 天克地冲
const _check_BS3_TianKeDiChong = (dayGanZhi, hourGanZhi) => {
  const dayGanIdx = _getGanIndex(dayGanZhi[0]);
  const hourGanIdx = _getGanIndex(hourGanZhi[0]);
  const dayZhiIdx = _getZhiIndex(dayGanZhi[1]);
  const hourZhiIdx = _getZhiIndex(hourGanZhi[1]);

  const isGanChong = Math.abs(dayGanIdx - hourGanIdx) === 6;
  const isZhiChong = Math.abs(dayZhiIdx - hourZhiIdx) === 6;

  return (isGanChong && isZhiChong) ? RULES_BAD.find(r => r.code === "BS3") : null;
};
// BN1: 日冲生肖
const _check_BN1_GanZhiChongNianMing = (ganZhi, zodiac) => {
  if (!ganZhi || !zodiac || !ZODIAC_MAP[zodiac]) return null;
  const zhi = ganZhi[1];
  const zodiacZhi = ZODIAC_MAP[zodiac];
  return (ZHI_CHONG_MAP[zodiacZhi] === zhi) ? RULES_BAD.find(r => r.code === "BN1") : null;
};
// GQ1: 天显时格
const _check_GQ1_TianXianShiGe = (ganZhi) => {
  const xunShous = ["甲子", "甲戌", "甲申", "甲午", "甲辰", "甲寅"];
  return xunShous.includes(ganZhi) ? RULES_GOOD.find(r => r.code === "GQ1") : null;
};
// GQ2: 玉女守门
const _check_GQ2_YuNuShouMen = (hourGan) => {
  // 简化版：仅匹配丁
  return (hourGan === "丁") ? RULES_GOOD.find(r => r.code === "GQ2") : null;
};
// 基础过滤条件：根据事项类型和时段过滤不相关的时辰（如搬家结婚多选上午，且不宜午后时辰）
const _check_BaseCondition = (index, type, timePeriod) => {
  if (timePeriod === "上午" && index > 5) return false;
  if (timePeriod === "下午" && index <= 5) return false;

  // 搬家/结婚：仅限 07:00 - 13:00 (辰4, 巳5, 午6)
  if ((type === "搬家" || type === "结婚") && (index < 4 || index > 6)) return false;

  return true;
};

// =========================================================================
// 4. 核心匹配逻辑 (CORE LOGIC)
// =========================================================================

/**
 * 匹配日维度的规则（如日支冲生肖、阴差阳错日等）
 * @param {Date} day - 日期对象
 * @param {string} zodiac - 生肖
 * @param {string} type - 事项类型
 * @param {string} timePeriod - 时段
 */
const _matchDayRules = (day, zodiac = "", type = "", timePeriod = "") => {
  const result = { good: [], bad: [] };
  if (!day) return result;

  const lunar = Lunar.fromDate(day);
  const dayGanZhi = lunar.getDayInGanZhi();

  // 1. 匹配 RULES_BAD
  // BN1: 日冲生肖
  const bn1 = _check_BN1_GanZhiChongNianMing(dayGanZhi, zodiac);
  if (bn1) result.bad.push(bn1);

  // BD1: 阴差阳错 (日柱)
  const bd1 = _check_BD1_YinChaYangCuo(dayGanZhi);
  if (bd1) result.bad.push(bd1);

    // 2. 匹配 RULES_GOOD
  const g1 = _check_GQ1_TianXianShiGe(dayGanZhi);
  if (g1) result.good.push(g1);
  

  return result;
};

const _matchHourRules = (hourGanZhi, zodiac = "", type = "", timePeriod = "") => {
  const result = { good: [], bad: [] };
  if (!hourGanZhi) return result;

  // 1. 匹配 RULES_BAD
  // BN1: 时辰冲生肖
  const bn1 = _check_BN1_GanZhiChongNianMing(hourGanZhi, zodiac);
  if (bn1) {
     // 可选择克隆 rule 对象并修改 name/desc 以适配时辰语境
     result.bad.push({ ...bn1, name: "时冲生肖", desc: "时支冲克用户属相" });
  }     

  // BD1: 阴差阳错 (时柱)
  const bd1 = _check_BD1_YinChaYangCuo(hourGanZhi);
  if (bd1) result.bad.push(bd1);

  // 2. 匹配 RULES_GOOD
  const g1 = _check_GQ1_TianXianShiGe(hourGanZhi);
  if (g1) result.good.push(g1);
  
  
  return result;
};

/**
 * 匹配日干与时辰维度的规则 (核心逻辑)
 * @param {string} dayGanZhi - 日柱干支 (用于关系判断)
 * @param {string} hourGanZhi - 时柱干支
 * @param {string} zodiac - 生肖
 * @param {string} type - 事项类型
 * @param {string} timePeriod - 时段
 */
const _matchDayHourRules = (dayGanZhi, hourGanZhi, zodiac = "", type = "", timePeriod = "") => {
  const result = { hour: hourGanZhi, bad: [], good: [] };
  if (!dayGanZhi || !hourGanZhi) return result;

  const dayGan = dayGanZhi[0];
  const dayZhi = dayGanZhi[1];
  const hourGan = hourGanZhi[0];
  const hourZhi = hourGanZhi[1];

  // 1. 匹配 RULES_BAD
  const b1 = _check_BS1_RiGanRuShiMu(dayGan, hourZhi);
  if (b1) result.bad.push(b1);

  const b2 = _check_BS2_WuBuYuShi(dayGan, hourGan);
  if (b2) result.bad.push(b2);

  const b3 = _check_BS3_TianKeDiChong(dayGanZhi, hourGanZhi);
  if (b3) result.bad.push(b3);


  return result;
};

// =========================================================================
// 5. 导出方法 (EXPORTED METHODS)
// =========================================================================

/**
 * 获取指定日期的全天12个时辰干支
 */
export const getShichenGanzhi = (date) => {
  if (!date) return [];
  const list = [];
  try {
    const d = new Date(date.getTime());
    d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 12; i++) {
      d.setHours(i * 2);
      const lunar = Lunar.fromDate(d);
      list.push(lunar.getTimeInGanZhi());
    }
    return list;
  } catch (e) {
    console.error("getShichen error", e);
    return [];
  }
};

/**
 * 兼容旧代码导出 matchRules
 */
export const matchRules = (dayGanZhi, hourGanZhi) => _matchDayHourRules(dayGanZhi, hourGanZhi); // 代理到新核心方法

/**
 * 分析指定日期的12个时辰，匹配吉凶规则 (含属相)
 */
export const analyzeDaymatchRules = (date, zodiac = "", type = "", timePeriod = "") => {
  if (!date) return { hours: [], dayLuck: [] };

  const lunar = Lunar.fromDate(date);
  const dayGanZhi = lunar.getDayInGanZhi();
  const hours = getShichenGanzhi(date);

  const timeRanges = [
    "23-01时", "01-03时", "03-05时", "05-07时",
    "07-09时", "09-11时", "11-13时", "13-15时",
    "15-17时", "17-19时", "19-21时", "21-23时"
  ];

  // 日柱级别凶煞 (只在总评显示)
  const dayMatchResult = _matchDayRules(date, zodiac, type, timePeriod);
  const dayBadMatches = dayMatchResult.bad;
  const dayGoodMatches = dayMatchResult.good;

  const schedule = hours.map((hourGanZhi, index) => {
    // 基础过滤
    if (!_check_BaseCondition(index, type, timePeriod)) return null;

    // 分别匹配日时关系规则 和 时辰单柱/属相规则
    const matchDH = _matchDayHourRules(dayGanZhi, hourGanZhi, zodiac, type, timePeriod);
    const matchH = _matchHourRules(hourGanZhi, zodiac, type, timePeriod);

    // 合并结果
    const bad = [...matchDH.bad, ...matchH.bad];
    const good = [...matchDH.good, ...matchH.good];

    return {
      hour: hourGanZhi,
      time: timeRanges[index],
      bad: bad,
      good: good,
    };
  }).filter(item => item !== null);

  return { hours: schedule, dayLuck: dayBadMatches, dayGoodLuck: dayGoodMatches };
};

/**
 * 获取日宜/日忌
 */
export const getDayYiJi = (date) => {
  if (!date) return { yi: [], ji: [] };
  try {
    const lunar = Lunar.fromDate(date);
    return { yi: lunar.getDayYi() || [], ji: lunar.getDayJi() || [] };
  } catch (e) {
    return { yi: [], ji: [] };
  }
};

/**
 * 汇总指定日期的吉凶时段
 */
export const buildTimeLuckSummary = (date, zodiac, type, timePeriod) => {
  const analysis = analyzeDaymatchRules(date, zodiac, type, timePeriod);
  
  const items = analysis.hours.map((item) => {
    const good = (item.good || []).map((rule) => rule.name);
    const bad = (item.bad || []).map((rule) => rule.name);
    
    let status = "neutral";
    if (good.length > 0 && bad.length > 0) status = "mixed";
    else if (good.length > 0) status = "good";
    else if (bad.length > 0) status = "bad";

    return {
      time: item.time,
      hour: item.hour,
      good,
      bad,
      status,
    };
  });

  return {
    items,
    dayLuck: analysis.dayLuck,
    dayGoodLuck: analysis.dayGoodLuck,
    goodTimes: items.filter((item) => item.good.length > 0),
    badTimes: items.filter((item) => item.bad.length > 0),
  };
};



